/**
 * Master org switcher for the admin shell.
 *
 * Sits in the top bar's `centerSlot` (see `AppTopBar.tsx`). Selecting
 * an org rewrites the URL so the current admin page stays the same
 * but is re-rooted under the new tenant — i.e. switching while
 * looking at `/admin/orgs/<a>/claims?status=denied` lands on
 * `/admin/orgs/<b>/claims?status=denied`.
 *
 * Design choices (locked in documentation.md §19h):
 *   - URL-prefix is the source of truth; this component never holds
 *     the "current org" in React state. We read it from `useParams`
 *     and write it to the URL via `navigate`.
 *   - The selected org id is mirrored into `localStorage.adminLastOrgId`
 *     so that subsequent admin sessions land on the last-used org
 *     (handled by `AdminOrgScope` in `AppRoutes.tsx`).
 *   - The dropdown is a simple `<select>` for accessibility + zero
 *     additional dependencies. We can swap to a richer combobox in
 *     a follow-up once the IA is settled.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAdminOrganizations } from '../../services/adminApi';
import type { AdminOrganizationData } from '../../services/types';

const ADMIN_LAST_ORG_KEY = 'adminLastOrgId';

interface AdminOrgSwitcherProps {
  /** Default landing page within the new org when no current
   *  `/admin/orgs/<oldId>/<tail>` segment is present. */
  defaultLanding?: string;
}

const AdminOrgSwitcher: React.FC<AdminOrgSwitcherProps> = ({
  defaultLanding = 'overview',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId: currentOrgId } = useParams<{ orgId?: string }>();

  const [orgs, setOrgs] = useState<AdminOrganizationData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    // 500 = the backend cap on `GET /admin/organizations` (see
    // admin_org_controller.list_organizations). If we ever cross it
    // we'll need a search/typeahead instead of a flat select anyway.
    getAdminOrganizations({ limit: 500 })
      .then((res) => {
        if (!alive) return;
        const list = (res.data || []).slice().sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
        setOrgs(list);
      })
      .catch((err) => {
        if (!alive) return;
        const msg = err instanceof Error ? err.message : 'Failed to load orgs';
        setError(msg);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    if (!newId || newId === currentOrgId) return;

    try {
      localStorage.setItem(ADMIN_LAST_ORG_KEY, newId);
    } catch {
      // localStorage can throw in private mode / quota — best-effort.
    }

    const { pathname, search, hash } = location;
    // Rewrite the existing /admin/orgs/<id>/<tail> segment in place
    // so the page (claims, payers, profile, etc.) stays selected.
    const swapped = pathname.replace(
      /\/admin\/orgs\/[^/]+/,
      `/admin/orgs/${newId}`
    );
    if (swapped !== pathname) {
      navigate({ pathname: swapped, search, hash });
      return;
    }

    // We weren't on a /admin/orgs/<id>/* page yet (e.g. /admin or
    // /admin/organizations) — drop the admin onto the default
    // landing inside the new org.
    navigate(`/admin/orgs/${newId}/${defaultLanding}`);
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      <label
        htmlFor="admin-org-switcher"
        className="text-[10px] uppercase tracking-[0.16em] text-theme-text-muted shrink-0"
      >
        Org
      </label>
      <select
        id="admin-org-switcher"
        value={currentOrgId ?? ''}
        onChange={handleChange}
        disabled={loading || orgs.length === 0}
        className="min-w-0 max-w-[260px] truncate rounded-full border border-dc-border bg-theme-surface px-3 py-1 text-[11px] leading-none text-theme-text hover:bg-theme-surface-hover focus:outline-none focus:ring-1 focus:ring-theme-accent disabled:opacity-60"
        aria-label="Select admin organization"
      >
        {!currentOrgId && (
          <option value="" disabled>
            {loading ? 'Loading orgs…' : 'Select an organization'}
          </option>
        )}
        {orgs.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
      {error ? (
        <span
          className="text-[10px] text-red-400 truncate"
          title={error}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default AdminOrgSwitcher;
export { ADMIN_LAST_ORG_KEY };
